// This file is the route configuration: it intentionally co-locates lazily
// loaded page components with the `router` export, so the fast-refresh
// "only export components" rule does not apply here.
/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense, type ReactElement } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { PageFallback } from "@/components/layout/PageFallback";
import { ProtectedLayout } from "@/components/layout/ProtectedLayout";
import { RouteErrorBoundary } from "@/components/layout/RouteErrorBoundary";
import { AuthGuard } from "@/components/guards/AuthGuard";
import { DashboardGuard } from "@/components/guards/DashboardGuard";
import { StatisticsGuard } from "@/components/guards/StatisticsGuard";
import { PlatformGuard } from "@/components/guards/PlatformGuard";
import { InsuranceGuard } from "@/features/insurances/components/InsuranceGuard";
import { SingleClinicGuard } from "@/features/organisations/components/SingleClinicGuard";
import { LegacyVenteRedirect } from "@/routes/LegacyVenteRedirect";
import { ArticlesOnlyRoute, LegacyCatalogueRoute, LegacyProductRoute } from "@/routes/ArticlesRoutes";

// Pages are lazy-loaded so the initial bundle (e.g. the login screen) does not
// pull in every screen of the application. Each page module exports its
// component as a named export, so we map it to `default` for React.lazy.

// Platform (éditeur GoldenTech) console
const PlatformOverviewPage = lazy(() =>
  import("@/pages/platform/PlatformOverviewPage").then((m) => ({
    default: m.PlatformOverviewPage,
  })),
);
const CustomersPage = lazy(() =>
  import("@/pages/platform/CustomersPage").then((m) => ({
    default: m.CustomersPage,
  })),
);
const PlatformStaffPage = lazy(() =>
  import("@/pages/platform/PlatformStaffPage").then((m) => ({
    default: m.PlatformStaffPage,
  })),
);
const SmsPackagesPage = lazy(() =>
  import("@/pages/platform/SmsPackagesPage").then((m) => ({
    default: m.SmsPackagesPage,
  })),
);
const LicensesPage = lazy(() =>
  import("@/pages/platform/LicensesPage").then((m) => ({
    default: m.LicensesPage,
  })),
);
const PlatformCustomerDetailPage = lazy(() =>
  import("@/pages/platform/CustomerDetailPage").then((m) => ({
    default: m.CustomerDetailPage,
  })),
);
const PlatformSettingsPage = lazy(() =>
  import("@/pages/platform/PlatformSettingsPage").then((m) => ({
    default: m.PlatformSettingsPage,
  })),
);
const PlatformDomainsPage = lazy(() =>
  import("@/pages/platform/DomainsPage").then((m) => ({
    default: m.DomainsPage,
  })),
);
const PlatformUsersPage = lazy(() =>
  import("@/pages/platform/PlatformUsersPage").then((m) => ({
    default: m.PlatformUsersPage,
  })),
);

// Clinique
const AppointmentDetailPage = lazy(() =>
  import("@/pages/AppointmentDetailPage").then((m) => ({
    default: m.AppointmentDetailPage,
  })),
);
const OnlineBookingPage = lazy(() =>
  import("@/pages/OnlineBookingPage").then((m) => ({
    default: m.OnlineBookingPage,
  })),
);
const AppointmentsPage = lazy(() =>
  import("@/pages/AppointmentsPage").then((m) => ({
    default: m.AppointmentsPage,
  })),
);
const ConsultationDetailPage = lazy(() =>
  import("@/pages/ConsultationDetailPage").then((m) => ({
    default: m.ConsultationDetailPage,
  })),
);
const ConsultationsPage = lazy(() =>
  import("@/pages/ConsultationsPage").then((m) => ({
    default: m.ConsultationsPage,
  })),
);
const SmsPage = lazy(() =>
  import("@/pages/SmsPage").then((m) => ({ default: m.SmsPage })),
);
const SmsPaymentReturnPage = lazy(() =>
  import("@/pages/SmsPaymentReturnPage").then((m) => ({ default: m.SmsPaymentReturnPage })),
);
const DashboardPage = lazy(() =>
  import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const StatisticsPage = lazy(() =>
  import("@/pages/StatisticsPage").then((m) => ({ default: m.StatisticsPage })),
);
const ExaminationsPage = lazy(() =>
  import("@/pages/ExaminationsPage").then((m) => ({
    default: m.ExaminationsPage,
  })),
);
const ExaminationFormPage = lazy(() =>
  import("@/pages/ExaminationFormPage").then((m) => ({
    default: m.ExaminationFormPage,
  })),
);
const EyePrescriptionDetailPage = lazy(() =>
  import("@/pages/EyePrescriptionDetailPage").then((m) => ({
    default: m.EyePrescriptionDetailPage,
  })),
);
const EyePrescriptionsPage = lazy(() =>
  import("@/pages/EyePrescriptionsPage").then((m) => ({
    default: m.EyePrescriptionsPage,
  })),
);
const PatientDetailPage = lazy(() =>
  import("@/pages/PatientDetailPage").then((m) => ({
    default: m.PatientDetailPage,
  })),
);
const PatientsListPage = lazy(() =>
  import("@/pages/PatientsListPage").then((m) => ({
    default: m.PatientsListPage,
  })),
);
const PrescriptionDetailPage = lazy(() =>
  import("@/pages/PrescriptionDetailPage").then((m) => ({
    default: m.PrescriptionDetailPage,
  })),
);
const PrescriptionsPage = lazy(() =>
  import("@/pages/PrescriptionsPage").then((m) => ({
    default: m.PrescriptionsPage,
  })),
);
const ClientDetailPage = lazy(() =>
  import("@/pages/ClientDetailPage").then((m) => ({
    default: m.ClientDetailPage,
  })),
);
const ClientsPage = lazy(() =>
  import("@/pages/ClientsPage").then((m) => ({ default: m.ClientsPage })),
);
const FamiliesPage = lazy(() =>
  import("@/pages/FamiliesPage").then((m) => ({ default: m.FamiliesPage })),
);

// Gestion
const ActsPage = lazy(() =>
  import("@/pages/ActsPage").then((m) => ({ default: m.ActsPage })),
);
const CataloguePage = lazy(() =>
  import("@/pages/CataloguePage").then((m) => ({ default: m.CataloguePage })),
);
const ProductDetailPage = lazy(() =>
  import("@/pages/ProductDetailPage").then((m) => ({
    default: m.ProductDetailPage,
  })),
);
const ArticlesPage = lazy(() =>
  import("@/pages/ArticlesPage").then((m) => ({ default: m.ArticlesPage })),
);
const MedicationsPage = lazy(() =>
  import("@/pages/MedicationsPage").then((m) => ({ default: m.MedicationsPage })),
);
const CategoriesPage = lazy(() =>
  import("@/pages/CategoriesPage").then((m) => ({ default: m.CategoriesPage })),
);
const CategoryDetailPage = lazy(() =>
  import("@/pages/CategoryDetailPage").then((m) => ({
    default: m.CategoryDetailPage,
  })),
);
const StockPage = lazy(() =>
  import("@/pages/StockPage").then((m) => ({ default: m.StockPage })),
);
const StockHomePage = lazy(() =>
  import("@/pages/StockHomePage").then((m) => ({ default: m.StockHomePage })),
);
const PurchasesPage = lazy(() =>
  import("@/pages/PurchasesPage").then((m) => ({ default: m.PurchasesPage })),
);
const SuppliersPage = lazy(() =>
  import("@/pages/SuppliersPage").then((m) => ({ default: m.SuppliersPage })),
);
const PurchaseOrderPage = lazy(() =>
  import("@/pages/PurchaseOrderPage").then((m) => ({ default: m.PurchaseOrderPage })),
);
const StockCountsPage = lazy(() =>
  import("@/pages/StockCountsPage").then((m) => ({ default: m.StockCountsPage })),
);
const StockControlPage = lazy(() =>
  import("@/pages/StockControlPage").then((m) => ({ default: m.StockControlPage })),
);
const StockCountPage = lazy(() =>
  import("@/pages/StockCountPage").then((m) => ({ default: m.StockCountPage })),
);
const StockTransfersPage = lazy(() =>
  import("@/pages/StockTransfersPage").then((m) => ({ default: m.StockTransfersPage })),
);
const StockTransferPage = lazy(() =>
  import("@/pages/StockTransferPage").then((m) => ({ default: m.StockTransferPage })),
);
const StockThresholdsPage = lazy(() =>
  import("@/pages/StockThresholdsPage").then((m) => ({ default: m.StockThresholdsPage })),
);
const GoodsReceiptPage = lazy(() =>
  import("@/pages/GoodsReceiptPage").then((m) => ({ default: m.GoodsReceiptPage })),
);
const SupplierReturnPage = lazy(() =>
  import("@/pages/SupplierReturnPage").then((m) => ({ default: m.SupplierReturnPage })),
);
const SupplierPayablesPage = lazy(() =>
  import("@/pages/SupplierPayablesPage").then((m) => ({ default: m.SupplierPayablesPage })),
);
const SupplierInvoicePage = lazy(() =>
  import("@/pages/SupplierInvoicePage").then((m) => ({ default: m.SupplierInvoicePage })),
);
const ExpensesPage = lazy(() =>
  import("@/pages/ExpensesPage").then((m) => ({ default: m.ExpensesPage })),
);
const ProfitPage = lazy(() =>
  import("@/pages/ProfitPage").then((m) => ({ default: m.ProfitPage })),
);
const SalesPage = lazy(() =>
  import("@/pages/SalesPage").then((m) => ({ default: m.SalesPage })),
);
const SaleCreatePage = lazy(() =>
  import("@/pages/SaleCreatePage").then((m) => ({ default: m.SaleCreatePage })),
);
const SaleDetailPage = lazy(() =>
  import("@/pages/SaleDetailPage").then((m) => ({ default: m.SaleDetailPage })),
);
const ProformasPage = lazy(() =>
  import("@/pages/ProformasPage").then((m) => ({ default: m.ProformasPage })),
);
const ProformaCreatePage = lazy(() =>
  import("@/pages/ProformaCreatePage").then((m) => ({
    default: m.ProformaCreatePage,
  })),
);
const ProformaDetailPage = lazy(() =>
  import("@/pages/ProformaDetailPage").then((m) => ({
    default: m.ProformaDetailPage,
  })),
);
const PaymentsPage = lazy(() =>
  import("@/pages/PaymentsPage").then((m) => ({ default: m.PaymentsPage })),
);
const PaymentDetailPage = lazy(() =>
  import("@/pages/PaymentDetailPage").then((m) => ({
    default: m.PaymentDetailPage,
  })),
);
const TreasuryPage = lazy(() =>
  import("@/pages/TreasuryPage").then((m) => ({
    default: m.TreasuryPage,
  })),
);
const CashClosurePage = lazy(() =>
  import("@/pages/CashClosurePage").then((m) => ({
    default: m.CashClosurePage,
  })),
);
const InsurancesPage = lazy(() =>
  import("@/pages/InsurancesPage").then((m) => ({ default: m.InsurancesPage })),
);
const InsuranceDetailPage = lazy(() =>
  import("@/pages/InsuranceDetailPage").then((m) => ({
    default: m.InsuranceDetailPage,
  })),
);
const InsuranceReportPage = lazy(() =>
  import("@/pages/InsuranceReportPage").then((m) => ({
    default: m.InsuranceReportPage,
  })),
);

// Administration
const BoutiquePage = lazy(() =>
  import("@/pages/admin/BoutiquePage").then((m) => ({
    default: m.BoutiquePage,
  })),
);
const BoutiqueDetailPage = lazy(() =>
  import("@/pages/admin/BoutiqueDetailPage").then((m) => ({
    default: m.BoutiqueDetailPage,
  })),
);
const SitesPage = lazy(() =>
  import("@/pages/admin/SitesPage").then((m) => ({ default: m.SitesPage })),
);
const AgenceDetailPage = lazy(() =>
  import("@/pages/admin/AgenceDetailPage").then((m) => ({
    default: m.AgenceDetailPage,
  })),
);
const UsersPage = lazy(() =>
  import("@/pages/admin/UsersPage").then((m) => ({ default: m.UsersPage })),
);
const UserDetailPage = lazy(() =>
  import("@/pages/admin/UserDetailPage").then((m) => ({
    default: m.UserDetailPage,
  })),
);
const OrganisationsPage = lazy(() =>
  import("@/pages/admin/OrganisationsPage").then((m) => ({
    default: m.OrganisationsPage,
  })),
);
const OrganisationDetailPage = lazy(() =>
  import("@/pages/admin/OrganisationDetailPage").then((m) => ({
    default: m.OrganisationDetailPage,
  })),
);
const RolesPage = lazy(() =>
  import("@/pages/admin/RolesPage").then((m) => ({ default: m.RolesPage })),
);
const RoleFormPage = lazy(() =>
  import("@/pages/admin/RoleFormPage").then((m) => ({ default: m.RoleFormPage })),
);
const RoleDetailPage = lazy(() =>
  import("@/pages/admin/RoleDetailPage").then((m) => ({
    default: m.RoleDetailPage,
  })),
);
const ExaminationTypesPage = lazy(() =>
  import("@/pages/admin/ExaminationTypesPage").then((m) => ({
    default: m.ExaminationTypesPage,
  })),
);
const AuditPage = lazy(() =>
  import("@/pages/AuditPage").then((m) => ({ default: m.AuditPage })),
);
const ReportsPage = lazy(() =>
  import("@/pages/admin/ReportsPage").then((m) => ({ default: m.ReportsPage })),
);

// Profil
const ProfilePage = lazy(() =>
  import("@/pages/ProfilePage").then((m) => ({ default: m.ProfilePage })),
);
const HelpCenterPage = lazy(() =>
  import("@/pages/HelpCenterPage").then((m) => ({ default: m.HelpCenterPage })),
);
const NotificationsPage = lazy(() =>
  import("@/pages/NotificationsPage").then((m) => ({
    default: m.NotificationsPage,
  })),
);
const ConfigurationPage = lazy(() =>
  import("@/pages/ConfigurationPage").then((m) => ({
    default: m.ConfigurationPage,
  })),
);
const SubscriptionPage = lazy(() =>
  import("@/pages/SubscriptionPage").then((m) => ({
    default: m.SubscriptionPage,
  })),
);
const RootPage = lazy(() =>
  import("@/pages/RootPage").then((m) => ({ default: m.RootPage })),
);
const SignupPage = lazy(() =>
  import("@/pages/SignupPage").then((m) => ({ default: m.SignupPage })),
);
const ChangePasswordPage = lazy(() =>
  import("@/pages/ChangePasswordPage").then((m) => ({
    default: m.ChangePasswordPage,
  })),
);

// Auth / standalone
const LoginPage = lazy(() =>
  import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/pages/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("@/pages/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const VerifyEmailPage = lazy(() =>
  import("@/pages/VerifyEmailPage").then((m) => ({
    default: m.VerifyEmailPage,
  })),
);
const OnboardingHandoffPage = lazy(() =>
  import("@/pages/OnboardingHandoffPage").then((m) => ({
    default: m.OnboardingHandoffPage,
  })),
);
const ForbiddenPage = lazy(() =>
  import("@/pages/ForbiddenPage").then((m) => ({ default: m.ForbiddenPage })),
);
const PrivacyPolicyPage = lazy(() =>
  import("@/pages/PrivacyPolicyPage").then((m) => ({
    default: m.PrivacyPolicyPage,
  })),
);
const LegalNoticePage = lazy(() =>
  import("@/pages/LegalNoticePage").then((m) => ({
    default: m.LegalNoticePage,
  })),
);
const TermsOfServicePage = lazy(() =>
  import("@/pages/TermsOfServicePage").then((m) => ({
    default: m.TermsOfServicePage,
  })),
);
const DataSecurityPage = lazy(() =>
  import("@/pages/DataSecurityPage").then((m) => ({
    default: m.DataSecurityPage,
  })),
);
const GuidePage = lazy(() =>
  import("@/pages/GuidePage").then((m) => ({ default: m.GuidePage })),
);
const NotFoundPage = lazy(() =>
  import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);

/**
 * Wraps a lazily-loaded page element in a Suspense boundary with the shared
 * centered spinner fallback. Used for every route `element` so guards keep
 * wrapping the pages while the chunk loads.
 */
function suspended(node: ReactElement): ReactElement {
  return <Suspense fallback={<PageFallback />}>{node}</Suspense>;
}

/** Écran propre à une clinique : demande d'en choisir une depuis « Toutes les
 *  cliniques » (ADR 0065). */
function clinicOnly(node: ReactElement): ReactElement {
  return <SingleClinicGuard>{suspended(node)}</SingleClinicGuard>;
}

export const router = createBrowserRouter([
  {
    // Frontière d'erreur globale : un chunk lazy qui échoue (déploiement) ou une
    // erreur de rendu affiche un écran de récupération au lieu d'un écran blanc.
    errorElement: <RouteErrorBoundary />,
    children: [
      // Compat : l'ancienne URL /accueil redirige vers la racine.
      { path: "/accueil", element: <Navigate to="/" replace /> },
      { path: "/inscription", element: suspended(<SignupPage />) },
      { path: "/login", element: suspended(<LoginPage />) },
      { path: "/editeur/login", element: suspended(<LoginPage editor />) },
      {
        path: "/mot-de-passe-oublie",
        element: suspended(<ForgotPasswordPage />),
      },
      {
        path: "/reinitialiser-mot-de-passe",
        element: suspended(<ResetPasswordPage />),
      },
      {
        path: "/definir-mot-de-passe",
        element: suspended(<ResetPasswordPage mode="invite" />),
      },
      // C4 — routes canoniques /auth/* : mêmes composants que les routes FR
      // historiques (alias de route, zéro duplication, aucun lien cassé).
      { path: "/auth/login", element: suspended(<LoginPage />) },
      { path: "/auth/register", element: suspended(<SignupPage />) },
      { path: "/auth/forgot", element: suspended(<ForgotPasswordPage />) },
      { path: "/auth/reset", element: suspended(<ResetPasswordPage />) },
      {
        path: "/auth/set-password",
        element: suspended(<ResetPasswordPage mode="invite" />),
      },
      // Vérification de l'adresse e-mail (lien reçu par e-mail).
      { path: "/auth/verify-email", element: suspended(<VerifyEmailPage />) },
      { path: "/verifier-email", element: suspended(<VerifyEmailPage />) },
      // Auto-login post-onboarding : échange le ticket signé contre une session.
      { path: "/auth/handoff", element: suspended(<OnboardingHandoffPage />) },
      // C12 — politique de confidentialité publique.
      { path: "/confidentialite", element: suspended(<PrivacyPolicyPage />) },
      // C-04 — mentions légales et conditions d'utilisation publiques.
      { path: "/mentions-legales", element: suspended(<LegalNoticePage />) },
      { path: "/conditions", element: suspended(<TermsOfServicePage />) },
      // M-08 — page "Sécurité et données", liée depuis la section Sécurité.
      { path: "/securite-des-donnees", element: suspended(<DataSecurityPage />) },
      { path: "/403", element: suspended(<ForbiddenPage />) },
      // Guide d'utilisation en ligne, réservé aux connectés (ADR 0046) : hors de
      // la mise en page de l'application (il a son propre en-tête), comme chez
      // UV-GTS. Sans session, la garde renvoie à la connexion.
      { path: "/guide", element: <AuthGuard>{suspended(<GuidePage />)}</AuthGuard> },
      {
        path: "/",
        children: [
          // Racine PUBLIQUE : vitrine pour les visiteurs (portail), login sur un
          // espace clinique, dashboard pour les utilisateurs connectés.
          { index: true, element: suspended(<RootPage />) },
          {
            // Layout protégé sans path : les routes filles restent sous "/".
            element: <ProtectedLayout />,
            children: [
              {
                path: "dashboard",
                element: (
                  <DashboardGuard>{suspended(<DashboardPage />)}</DashboardGuard>
                ),
              },
              {
                path: "statistiques",
                element: (
                  <StatisticsGuard>{suspended(<StatisticsPage />)}</StatisticsGuard>
                ),
              },
              // Clinique
              { path: "patients", element: suspended(<PatientsListPage />) },
              {
                path: "patients/:id",
                element: suspended(<PatientDetailPage />),
              },
              { path: "clients", element: suspended(<ClientsPage />) },
              { path: "clients/:id", element: suspended(<ClientDetailPage />) },
              { path: "familles", element: suspended(<FamiliesPage />) },
              { path: "rendez-vous", element: suspended(<AppointmentsPage />) },
              { path: "rendez-vous/en-ligne", element: suspended(<OnlineBookingPage />) },
              {
                path: "rendez-vous/:id",
                element: suspended(<AppointmentDetailPage />),
              },
              {
                path: "consultations",
                element: suspended(<ConsultationsPage />),
              },
              {
                path: "consultations/:id",
                element: suspended(<ConsultationDetailPage />),
              },
              { path: "examens", element: suspended(<ExaminationsPage />) },
              // L'ajout/édition du bulletin se fait en page pleine (workspace).
              {
                path: "examens/nouveau",
                element: suspended(<ExaminationFormPage />),
              },
              {
                path: "examens/:id/modifier",
                element: suspended(<ExaminationFormPage />),
              },
              {
                path: "prescriptions",
                element: suspended(<PrescriptionsPage />),
              },
              {
                path: "prescriptions/:id",
                element: suspended(<PrescriptionDetailPage />),
              },
              {
                path: "eye-prescriptions",
                element: suspended(<EyePrescriptionsPage />),
              },
              {
                path: "eye-prescriptions/:id",
                element: suspended(<EyePrescriptionDetailPage />),
              },
              // Gestion
              { path: "actes", element: suspended(<ActsPage />) },
              {
                path: "catalogue",
                element: (
                  <LegacyCatalogueRoute legacy="catalogue">{suspended(<CataloguePage />)}</LegacyCatalogueRoute>
                ),
              },
              {
                path: "catalogue/:id",
                element: <LegacyProductRoute>{suspended(<ProductDetailPage />)}</LegacyProductRoute>,
              },
              {
                path: "categories",
                element: (
                  <LegacyCatalogueRoute legacy="categories">{suspended(<CategoriesPage />)}</LegacyCatalogueRoute>
                ),
              },
              // Espace Articles (ADR 0062, module catalog_plus)
              {
                path: "articles",
                element: <ArticlesOnlyRoute>{suspended(<ArticlesPage />)}</ArticlesOnlyRoute>,
              },
              {
                path: "articles/:id",
                element: <ArticlesOnlyRoute detail>{suspended(<ProductDetailPage />)}</ArticlesOnlyRoute>,
              },
              {
                path: "categories/:id",
                element: suspended(<CategoryDetailPage />),
              },
              { path: "stock", element: clinicOnly(<StockPage />) },
              // Stock et inventaire avancés (ADR 0060)
              { path: "stock/inventaires", element: clinicOnly(<StockCountsPage />) },
              { path: "stock/inventaires/:id", element: clinicOnly(<StockCountPage />) },
              { path: "stock/transferts", element: clinicOnly(<StockTransfersPage />) },
              { path: "stock/transferts/:id", element: clinicOnly(<StockTransferPage />) },
              { path: "stock/seuils", element: clinicOnly(<StockThresholdsPage />) },
              { path: "stock/controle", element: clinicOnly(<StockControlPage />) },
              // Tableau de bord du stock (ADR 0061, module navigation_v2)
              { path: "stock/accueil", element: clinicOnly(<StockHomePage />) },
              // Achats et fournisseurs (ADR 0054)
              { path: "achats", element: clinicOnly(<PurchasesPage />) },
              {
                path: "achats/commandes/:id",
                element: clinicOnly(<PurchaseOrderPage />),
              },
              {
                path: "achats/receptions/:id",
                element: clinicOnly(<GoodsReceiptPage />),
              },
              {
                path: "achats/retours/:id",
                element: clinicOnly(<SupplierReturnPage />),
              },
              { path: "fournisseurs", element: clinicOnly(<SuppliersPage />) },
              { path: "factures", element: suspended(<SalesPage />) },
              {
                path: "factures/nouvelle",
                element: suspended(<SaleCreatePage />),
              },
              // Saisie des lignes d'un document déjà créé par la modale
              // « l'essentiel ». Doit précéder `factures/:id`, sinon la fiche
              // capterait l'URL.
              {
                path: "factures/:id/lignes",
                element: suspended(<SaleCreatePage />),
              },
              { path: "factures/:id", element: suspended(<SaleDetailPage />) },
              // Redirections de l'ancienne URL /ventes → /factures (favoris, anciens liens).
              { path: "ventes", element: <Navigate to="/factures" replace /> },
              {
                path: "ventes/nouvelle",
                element: <Navigate to="/factures/nouvelle" replace />,
              },
              { path: "ventes/:id", element: <LegacyVenteRedirect /> },
              { path: "proformas", element: suspended(<ProformasPage />) },
              {
                path: "proformas/nouvelle",
                element: suspended(<ProformaCreatePage />),
              },
              {
                path: "proformas/:id/lignes",
                element: suspended(<ProformaCreatePage />),
              },
              // Alias historique de `proformas/:id/lignes` — même écran. Plus
              // aucun bouton n'y mène (les fiches pointent sur `/lignes`), mais
              // des favoris et des liens partagés l'utilisent encore.
              {
                path: "proformas/:id/modifier",
                element: suspended(<ProformaCreatePage />),
              },
              {
                path: "proformas/:id",
                element: suspended(<ProformaDetailPage />),
              },
              { path: "paiements", element: suspended(<PaymentsPage />) },
              {
                path: "paiements/:id",
                element: suspended(<PaymentDetailPage />),
              },
              {
                path: "caisse/cloture",
                element: clinicOnly(<CashClosurePage />),
              },
              {
                path: "tresorerie",
                element: clinicOnly(<TreasuryPage />),
              },
              // Dettes fournisseurs (ADR 0056)
              {
                path: "dettes-fournisseurs",
                element: clinicOnly(<SupplierPayablesPage />),
              },
              {
                path: "dettes-fournisseurs/:id",
                element: clinicOnly(<SupplierInvoicePage />),
              },
              // Dépenses complètes (ADR 0057)
              { path: "depenses", element: clinicOnly(<ExpensesPage />) },
              // Résultat par agence (ADR 0059)
              { path: "resultat", element: suspended(<ProfitPage />) },
              // Module « Assurances et tiers payant » (ADR 0063).
              {
                path: "assurances",
                element: <InsuranceGuard>{suspended(<InsurancesPage />)}</InsuranceGuard>,
              },
              {
                path: "assurances/rapport",
                element: <InsuranceGuard>{suspended(<InsuranceReportPage />)}</InsuranceGuard>,
              },
              {
                path: "assurances/:id",
                element: <InsuranceGuard>{suspended(<InsuranceDetailPage />)}</InsuranceGuard>,
              },
              { path: "sms", element: suspended(<SmsPage />) },
              { path: "sms/paiement", element: suspended(<SmsPaymentReturnPage />) },
              // Administration
              { path: "admin/boutique", element: clinicOnly(<BoutiquePage />) },
              { path: "admin/boutique/:id", element: clinicOnly(<BoutiqueDetailPage />) },
              { path: "admin/sites", element: suspended(<SitesPage />) },
              {
                path: "admin/sites/:id",
                element: suspended(<AgenceDetailPage />),
              },
              { path: "admin/utilisateurs", element: suspended(<UsersPage />) },
              {
                path: "admin/utilisateurs/:id",
                element: suspended(<UserDetailPage />),
              },
              {
                path: "admin/organisations",
                element: suspended(<OrganisationsPage />),
              },
              {
                path: "admin/organisations/nouvelle",
                element: suspended(<OrganisationsPage />),
              },
              {
                // Fiche d'une clinique du compte : données de l'abonné, pas
                // la console éditeur. Le propriétaire du compte y accède sans
                // l'enrôlement 2FA du personnel GoldenTech (PlatformGuard).
                path: "admin/organisations/:id",
                element: suspended(<OrganisationDetailPage />),
              },
              { path: "admin/roles", element: suspended(<RolesPage />) },
              { path: "admin/roles/nouveau", element: suspended(<RoleFormPage />) },
              {
                path: "admin/roles/:id/modifier",
                element: suspended(<RoleFormPage />),
              },
              {
                path: "admin/roles/:id",
                element: suspended(<RoleDetailPage />),
              },
              {
                path: "admin/types-examen",
                element: suspended(<ExaminationTypesPage />),
              },
              { path: "admin/medicaments", element: suspended(<MedicationsPage />) },
              { path: "admin/audit", element: suspended(<AuditPage />) },
              { path: "admin/rapports", element: suspended(<ReportsPage />) },
              {
                path: "notifications",
                element: suspended(<NotificationsPage />),
              },
              { path: "abonnement", element: suspended(<SubscriptionPage />) },
              { path: "aide", element: suspended(<HelpCenterPage />) },
              // L'ancien self-service a fusionné dans le hub : on redirige au
              // lieu de tomber en 404 (liens, favoris, historiques).
              { path: "parametres", element: <Navigate to="/configuration" replace /> },
              // Centre de configuration UNIQUE. La page s'auto-garde via
              // `canSeeConfigHub` : super admin de l'abonné ou éditeur GTS.
              {
                path: "configuration",
                element: suspended(<ConfigurationPage />),
              },
              { path: "profil", element: suspended(<ProfilePage />) },
              {
                path: "profil/password",
                element: suspended(<ChangePasswordPage />),
              },
              // Console éditeur GoldenTech (réservée is_platform_staff).
              {
                path: "editeur",
                element: (
                  <PlatformGuard>
                    {suspended(<PlatformOverviewPage />)}
                  </PlatformGuard>
                ),
              },
              {
                path: "editeur/clients",
                element: (
                  <PlatformGuard>{suspended(<CustomersPage />)}</PlatformGuard>
                ),
              },
              {
                path: "editeur/clients/:id",
                element: (
                  <PlatformGuard>
                    {suspended(<PlatformCustomerDetailPage />)}
                  </PlatformGuard>
                ),
              },
              {
                path: "editeur/utilisateurs",
                element: (
                  <PlatformGuard>
                    {suspended(<PlatformUsersPage />)}
                  </PlatformGuard>
                ),
              },
              {
                path: "editeur/licences",
                element: (
                  <PlatformGuard>{suspended(<LicensesPage />)}</PlatformGuard>
                ),
              },
              {
                path: "editeur/packs-sms",
                element: (
                  <PlatformGuard>{suspended(<SmsPackagesPage />)}</PlatformGuard>
                ),
              },
              {
                path: "editeur/equipe",
                element: (
                  <PlatformGuard>
                    {suspended(<PlatformStaffPage />)}
                  </PlatformGuard>
                ),
              },
              // C8 — gestion des domaines racine SaaS depuis la console.
              {
                path: "editeur/domaines",
                element: (
                  <PlatformGuard>
                    {suspended(<PlatformDomainsPage />)}
                  </PlatformGuard>
                ),
              },
              {
                path: "editeur/parametres",
                element: (
                  <PlatformGuard>
                    {suspended(<PlatformSettingsPage />)}
                  </PlatformGuard>
                ),
              },
            ],
          },
        ],
      },
      { path: "*", element: suspended(<NotFoundPage />) },
    ],
  },
]);
